import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Facebook, Instagram, Twitter, Linkedin,
  Phone, Mail, MapPin, MessageCircle,
  ArrowRight, CheckCircle, Clock,
  ChevronRight, Leaf, Zap, Star, Users,
} from 'lucide-react';
import { motion } from 'framer-motion';
import visaLogo from "../images/visa.jpg";
import ApplycardLogo from "../images/apple-pay-black-white-mdr01qmuahr68svo.jpg";
import masterCardLogo from "../images/WhatsApp Image 2025-09-02 at 12.52.03_a8d18c33.jpg";

/* ── Reusable animated link ─────────────────────────────────────────────── */
const FooterLink = ({ to, children, external }) => {
  const cls =
    'group flex items-center gap-2 text-gray-400 hover:text-lime-400 text-sm transition-colors duration-200 py-1';
  return external ? (
    <a href={to} target="_blank" rel="noopener noreferrer" className={cls}>
      <ChevronRight size={13} className="text-lime-600 group-hover:translate-x-0.5 transition-transform duration-200" />
      {children}
    </a>
  ) : (
    <Link to={to} className={cls}>
      <ChevronRight size={13} className="text-lime-600 group-hover:translate-x-0.5 transition-transform duration-200" />
      {children}
    </Link>
  );
};

/* ── Social icon button ─────────────────────────────────────────────────── */
const SocialBtn = ({ Icon, href, label, gradient }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={label}
    className="group relative w-10 h-10 rounded-xl flex items-center justify-center bg-white/5 border border-white/10 hover:border-lime-500/40 transition-all duration-300 hover:scale-110 overflow-hidden"
  >
    <div
      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
      style={{ background: gradient }}
    />
    <Icon size={18} className="text-gray-400 group-hover:text-white relative z-10 transition-colors duration-300" />
  </a>
);

/* ── Stat card ──────────────────────────────────────────────────────────── */
const StatCard = ({ Icon, value, label }) => (
  <div className="flex items-center gap-3">
    <div className="w-10 h-10 rounded-xl bg-lime-500/15 border border-lime-500/25 flex items-center justify-center flex-shrink-0">
      <Icon size={18} className="text-lime-400" />
    </div>
    <div>
      <p className="text-white font-bold text-lg leading-none">{value}</p>
      <p className="text-gray-500 text-xs mt-0.5">{label}</p>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════════
   MAIN FOOTER
══════════════════════════════════════════════════════════════════════════ */
const FooterPage = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email.trim()) {
      setSubscribed(true);
      setEmail('');
      setTimeout(() => setSubscribed(false), 3500);
    }
  };

  return (
    <footer
      className="relative overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #071a0e 0%, #040f08 60%, #020a05 100%)' }}
    >
      {/* ── Top gradient separator ──────────────────────────────────────── */}
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{
          background:
            'linear-gradient(90deg, transparent 0%, #16a34a 20%, #84cc16 50%, #16a34a 80%, transparent 100%)',
        }}
      />

      {/* ── Subtle radial glow ──────────────────────────────────────────── */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at 50% 0%, rgba(22,163,74,0.08) 0%, transparent 70%)',
        }}
      />

      {/* ══════════════════════════════════════════════════════════════════
          STATS STRIP
      ══════════════════════════════════════════════════════════════════ */}
      <div
        className="border-b"
        style={{ borderColor: 'rgba(255,255,255,0.05)' }}
      >
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <StatCard Icon={Users} value="10,000+" label="Happy Customers" />
            <StatCard Icon={Leaf} value="100%" label="Fresh Ingredients" />
            <StatCard Icon={Zap} value="Daily" label="On-Time Delivery" />
            <StatCard Icon={Star} value="4.9 ★" label="Average Rating" />
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          MAIN FOOTER GRID
      ══════════════════════════════════════════════════════════════════ */}
      <div className="max-w-7xl mx-auto px-6 py-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10">

        {/* ── Column 1: Brand (spans 2 cols on lg) ─────────────────────── */}
        <div className="lg:col-span-2 space-y-6">
          {/* Brand wordmark */}
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center font-black text-lg"
                style={{
                  background: 'linear-gradient(135deg, #16a34a 0%, #84cc16 100%)',
                  color: '#fff',
                }}
              >
                DF
              </div>
              <span
                className="text-2xl font-extrabold tracking-tight"
                style={{
                  background: 'linear-gradient(90deg, #ffffff 0%, #86efac 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                DailyFit
              </span>
            </div>
            <p className="text-xs font-semibold uppercase tracking-widest text-lime-500/70 mb-3">
              Your Fitness Journey Starts Here
            </p>
            <p className="text-gray-400 text-sm leading-relaxed max-w-xs">
              Dubai's leading healthy meal delivery service — chef-prepared,
              nutritionist-approved meals delivered fresh to your door every morning.
            </p>
          </div>

          {/* Social Icons */}
          <div>
            <p className="text-white text-xs font-semibold uppercase tracking-widest mb-3">
              Follow Us
            </p>
            <div className="flex items-center gap-2">
              <SocialBtn
                Icon={Facebook}
                href="https://facebook.com"
                label="Facebook"
                gradient="linear-gradient(135deg, #1877F2, #0a56cc)"
              />
              <SocialBtn
                Icon={Instagram}
                href="https://instagram.com"
                label="Instagram"
                gradient="linear-gradient(135deg, #f58529, #dd2a7b, #515bd4)"
              />
              <SocialBtn
                Icon={Twitter}
                href="https://twitter.com"
                label="Twitter / X"
                gradient="linear-gradient(135deg, #1DA1F2, #0d8ecf)"
              />
              <SocialBtn
                Icon={Linkedin}
                href="https://linkedin.com"
                label="LinkedIn"
                gradient="linear-gradient(135deg, #0A66C2, #084e9a)"
              />
              <SocialBtn
                Icon={MessageCircle}
                href="https://wa.me/971562198110"
                label="WhatsApp"
                gradient="linear-gradient(135deg, #25D366, #128C7E)"
              />
            </div>
          </div>

          {/* Working Hours */}
          <div
            className="flex items-start gap-3 rounded-xl p-4 border"
            style={{
              background: 'rgba(255,255,255,0.03)',
              borderColor: 'rgba(255,255,255,0.07)',
            }}
          >
            <Clock size={16} className="text-lime-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-white text-sm font-semibold mb-1">Delivery Hours</p>
              <p className="text-gray-400 text-xs">Monday – Saturday: 6 AM – 9 AM</p>
              <p className="text-gray-400 text-xs">Order before 8 PM for next-day delivery</p>
            </div>
          </div>
        </div>

        {/* ── Column 2: Quick Links ─────────────────────────────────────── */}
        <div>
          <h3 className="text-white font-bold text-sm uppercase tracking-widest mb-5 flex items-center gap-2">
            <span
              className="w-4 h-0.5 rounded"
              style={{ background: 'linear-gradient(90deg, #16a34a, #84cc16)' }}
            />
            Quick Links
          </h3>
          <nav className="space-y-1">
            <FooterLink to="/">Home</FooterLink>
            <FooterLink to="/about">About Us</FooterLink>
            <FooterLink to="/meal-plans">Meal Plans</FooterLink>
            <FooterLink to="/checkout">Order Now</FooterLink>
            <FooterLink to="/contact">Contact Us</FooterLink>
            <FooterLink to="/terms">Terms & Conditions</FooterLink>
            <FooterLink to="/privacy">Privacy Policy</FooterLink>
          </nav>
        </div>

        {/* ── Column 3: Meal Plans ──────────────────────────────────────── */}
        <div>
          <h3 className="text-white font-bold text-sm uppercase tracking-widest mb-5 flex items-center gap-2">
            <span
              className="w-4 h-0.5 rounded"
              style={{ background: 'linear-gradient(90deg, #16a34a, #84cc16)' }}
            />
            Our Plans
          </h3>
          <nav className="space-y-1">
            <FooterLink to="/meal-plans/keto">Keto Diet</FooterLink>
            <FooterLink to="/meal-plans/vegan">Vegan Plan</FooterLink>
            <FooterLink to="/meal-plans/balanced">Balanced Meals</FooterLink>
            <FooterLink to="/meal-plans/muscle">Muscle Building</FooterLink>
            <FooterLink to="/meal-plans/weight-loss">Weight Loss</FooterLink>
            <FooterLink to="/meal-plans/custom">Custom Plan</FooterLink>
          </nav>

          {/* Certifications */}
          <div className="mt-6 space-y-2">
            {['Nutritionist Approved', 'HACCP Certified Kitchen', 'No Preservatives'].map(
              (cert) => (
                <div key={cert} className="flex items-center gap-2">
                  <CheckCircle size={13} className="text-lime-400 flex-shrink-0" />
                  <span className="text-gray-400 text-xs">{cert}</span>
                </div>
              )
            )}
          </div>
        </div>

        {/* ── Column 4: Contact ─────────────────────────────────────────── */}
        <div>
          <h3 className="text-white font-bold text-sm uppercase tracking-widest mb-5 flex items-center gap-2">
            <span
              className="w-4 h-0.5 rounded"
              style={{ background: 'linear-gradient(90deg, #16a34a, #84cc16)' }}
            />
            Contact Us
          </h3>

          <div className="space-y-4">
            {/* Address */}
            <div className="flex items-start gap-3">
              <MapPin size={15} className="text-lime-400 mt-0.5 flex-shrink-0" />
              <p className="text-gray-400 text-sm leading-relaxed">
                Dubai, United Arab Emirates
              </p>
            </div>

            {/* Phone */}
            <div className="flex items-center gap-3">
              <Phone size={15} className="text-lime-400 flex-shrink-0" />
              <div>
                <a href="tel:+97145654390" className="text-gray-400 text-sm hover:text-lime-400 transition-colors block">
                  +971 4 565 4390
                </a>
                <a href="tel:+971562198110" className="text-gray-400 text-sm hover:text-lime-400 transition-colors block">
                  +971 56 219 8110
                </a>
              </div>
            </div>

            {/* Email */}
            <div className="flex items-center gap-3">
              <Mail size={15} className="text-lime-400 flex-shrink-0" />
              <a
                href="mailto:support@dailyfit.ae"
                className="text-gray-400 text-sm hover:text-lime-400 transition-colors"
              >
                support@dailyfit.ae
              </a>
            </div>

            {/* WhatsApp CTA */}
            <a
              href="https://wa.me/971562198110"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 mt-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 hover:scale-[1.03] border group"
              style={{
                background: 'rgba(37,211,102,0.08)',
                borderColor: 'rgba(37,211,102,0.25)',
                color: '#25D366',
              }}
            >
              <MessageCircle size={15} />
              Chat on WhatsApp
              <ArrowRight size={13} className="ml-auto group-hover:translate-x-0.5 transition-transform" />
            </a>
          </div>

          {/* Newsletter */}
          <div className="mt-6">
            <p className="text-white text-xs font-bold uppercase tracking-widest mb-3">
              Newsletter
            </p>
            {subscribed ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex items-center gap-2 text-lime-400 text-sm font-medium py-2"
              >
                <CheckCircle size={16} />
                Thanks! You're subscribed. 🎉
              </motion.div>
            ) : (
              <form onSubmit={handleSubscribe} className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="flex-1 min-w-0 px-3 py-2 rounded-xl text-sm text-white placeholder-gray-500 outline-none border transition-colors duration-200 focus:border-lime-500/50"
                  style={{
                    background: 'rgba(255,255,255,0.05)',
                    borderColor: 'rgba(255,255,255,0.1)',
                  }}
                  required
                />
                <button
                  type="submit"
                  className="px-3 py-2 rounded-xl font-bold text-sm transition-all duration-200 hover:scale-105 flex-shrink-0"
                  style={{
                    background: 'linear-gradient(135deg, #16a34a, #84cc16)',
                    color: '#fff',
                  }}
                >
                  <ArrowRight size={16} />
                </button>
              </form>
            )}
            <p className="text-gray-600 text-[11px] mt-2">
              Get weekly nutrition tips & exclusive offers.
            </p>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          BOTTOM BAR
      ══════════════════════════════════════════════════════════════════ */}
      <div
        className="border-t"
        style={{ borderColor: 'rgba(255,255,255,0.06)' }}
      >
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">

            {/* Copyright */}
            <p className="text-gray-500 text-sm text-center md:text-left">
              © {new Date().getFullYear()}{' '}
              <span className="text-gray-300 font-semibold">DailyFit</span>. All rights reserved.
              {' '}Made with{' '}
              <span className="text-red-400">♥</span>
              {' '}in Dubai, UAE
            </p>

            {/* Center: Legal links */}
            <div className="flex items-center gap-4 flex-wrap justify-center">
              <Link to="/terms" className="text-gray-500 hover:text-lime-400 text-xs transition-colors">
                Terms
              </Link>
              <span className="text-gray-700">·</span>
              <Link to="/privacy" className="text-gray-500 hover:text-lime-400 text-xs transition-colors">
                Privacy
              </Link>
              <span className="text-gray-700">·</span>
              <Link to="/contact" className="text-gray-500 hover:text-lime-400 text-xs transition-colors">
                Support
              </Link>
            </div>

            {/* Right: Payment methods */}
            <div className="flex items-center gap-3">
              <span className="text-gray-600 text-[11px] uppercase tracking-widest">
                We Accept
              </span>
              <div className="flex items-center gap-2">
                <div className="bg-white rounded px-1.5 py-1 shadow">
                  <img src={visaLogo} alt="Visa" className="h-4 w-auto object-contain" />
                </div>
                <div className="bg-white rounded px-1.5 py-1 shadow">
                  <img src={masterCardLogo} alt="MasterCard" className="h-4 w-auto object-contain" />
                </div>
                <div className="bg-white rounded px-1.5 py-1 shadow">
                  <img src={ApplycardLogo} alt="Apple Pay" className="h-4 w-auto object-contain" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </footer>
  );
};

export default FooterPage;
